
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';

interface CardInsightsProps {
  insights: string[];
  onChange: (insights: string[]) => void;
}

const CardInsights: React.FC<CardInsightsProps> = ({ insights = [], onChange }) => {
  const [newInsight, setNewInsight] = useState('');

  const handleAddInsight = () => {
    if (newInsight.trim() === '') return;
    
    const updatedInsights = [...insights, newInsight.trim()];
    onChange(updatedInsights);
    setNewInsight('');
  };

  const handleRemoveInsight = (index: number) => {
    const updatedInsights = [...insights];
    updatedInsights.splice(index, 1);
    onChange(updatedInsights);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddInsight();
    }
  };

  return (
    <div className="space-y-4">
      <Label>Key Insights</Label>
      
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            value={newInsight}
            onChange={(e) => setNewInsight(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a key insight or takeaway"
          />
        </div>
        <Button 
          type="button" 
          onClick={handleAddInsight}
          disabled={newInsight.trim() === ''}
          size="sm"
        >
          <Plus size={16} className="mr-1" />
          Add
        </Button>
      </div>

      {insights.length > 0 && (
        <div className="mt-2">
          <div className="text-sm text-gray-500 mb-2">
            {insights.length} insight{insights.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-2">
            {insights.map((insight, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-2 bg-gray-50 rounded border"
              >
                <div className="text-sm">{insight}</div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleRemoveInsight(index)}
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardInsights;
